import { TokenService } from './token.service';
import { ToastService } from './toast.service';
import { Router } from '@angular/router';
import {
  ForgotPasswordRqDto,
  ForgotPasswordRsDto,
  LoginRqDto,
  LoginRsDto,
  ResetPasswordRqDto,
  ResetPasswordRsDto,
  TokenDeliveryDto,
} from './../../Models/Auth/Auth';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpRequestService } from './http-request.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { isPlatformBrowser } from '@angular/common';
import { Permission } from 'src/app/Models/Permission/Permission';
import { User } from 'src/app/Models/User/User';

const helper = new JwtHelperService();

export function tokenGetter() {
  try {
    const value = sessionStorage.getItem('access_token');
    let rawToken = value == 'null' ? null : value;

    if (!rawToken) {
      return null;
    }

    const isExpired = helper.isTokenExpired(rawToken);

    if (isExpired) {
      sessionStorage.setItem('access_token', null);

      return null;
    }

    return rawToken;
  } catch {
    return null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private controllerUrl = `${environment.baseUrl}/Auth`;
  private isWarningActive = false;
  private sessionWarningTime = 10 * 60 * 1000;
  private permissions: Permission;
  public user: User = null;

  constructor(
    private http: HttpRequestService,
    private router: Router,
    private toastService: ToastService,
    private tokenService: TokenService
  ) {
    setInterval(() => {
      this.checkSession();
    }, 1000);
  }

  public async login(request: LoginRqDto): Promise<LoginRsDto> {
    return new Promise<LoginRsDto>(async (resolve, reject) => {
      let serviceResponse: LoginRsDto = null;

      await this.http
        .request<LoginRsDto>(
          `${this.controllerUrl}/Login`,
          request,
          'GET',
          true
        )
        .then((response) => {
          if (response.status === 200) {
            serviceResponse = response.body;

            this.setActiveUser(serviceResponse.User);

            this.setToken(response.body.Token);
            this.setPermissions(response.body.User.Permission);
          } else {
            return reject(response.error);
          }

          resolve(serviceResponse);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public async forgotPassword(
    request: ForgotPasswordRqDto
  ): Promise<ForgotPasswordRsDto> {
    return new Promise<ForgotPasswordRsDto>(async (resolve, reject) => {
      let serviceResponse: ForgotPasswordRsDto = null;

      await this.http
        .request<ForgotPasswordRsDto>(
          `${this.controllerUrl}/ForgotPassword`,
          request,
          'GET',
          true
        )
        .then((response) => {
          if (response.status === 200) {
            serviceResponse = response.body;
            this.setSessionToken(serviceResponse.SessionToken)
          } else {
            return reject(response.error);
          }

          resolve(serviceResponse);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public refreshToken(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      this.tokenService
        .refreshToken({ token: this.getAuthToken() })
        .then((response) => {
          this.setToken(response.token);

          resolve();
        })
        .catch((error) => {
          this.closeDownSession();

          reject(error);
        });
    });
  }

  private setToken(token: string) {
    //if (isPlatformBrowser(this.))
    sessionStorage.setItem('access_token', token);
  }

  public getAuthToken(): string {
    return tokenGetter();
  }

  public clearSession(): void {
    this.clearToken();
    this.clearPermissions();
  }

  private checkSession(): void {
    const token = tokenGetter();

    if (!token) {
      return;
    }

    const expirationTime = helper.getTokenExpirationDate(token);

    const now = Math.floor(new Date().getTime());
    const then = Math.floor(new Date(expirationTime).getTime());

    const diff = then - now;

    if (diff <= this.sessionWarningTime && !this.isWarningActive) {
      this.isWarningActive = true;

      this.toastService
        .presentToastWithOptions(
          null,
          `Your session will expire in less than ${
            this.sessionWarningTime / 60 / 1000
          } minutes.`,
          '',
          30000,
          [
            {
              text: 'Extend',
              side: 'end',
              role: 'extend',
            },
          ],
          'bottom',
          'time',
          'warning'
        )
        .then((role) => {
          if (role === 'extend') {
            this.refreshToken()
              .then(() => {
                this.isWarningActive = false;

                this.toastService.presentToastWithOptions(
                  null,
                  'Your session has been extended.',
                  '',
                  3000,
                  [
                    {
                      text: 'Dismiss',
                      side: 'end',
                      role: 'dismiss',
                    },
                  ],
                  'bottom',
                  'information-circle',
                  'success'
                );

                this.checkSession();
              })
              .catch(() => {
                this.closeDownSession();
              });

            return;
          }

          this.closeDownSession();
        });
    }
  }

  /**
   * Closes down the session and redirects to the login page.
   *
   * @private
   * @memberof AuthService
   */
  private closeDownSession(): void {
    this.isWarningActive = false;

    this.clearSession();

    this.router.navigate(['/login']);

    this.toastService.presentToastWithOptions(
      null,
      'Your session has expired. Please log in again.',
      '',
      999999999,
      [
        {
          text: 'Dismiss',
          side: 'end',
          role: 'dismiss',
        },
      ],
      'bottom',
      'warning',
      'danger'
    );
  }

  private clearToken(): void {
    sessionStorage.setItem('access_token', '');
  }

  private setPermissions(userPermissions: Permission) {
    this.permissions = userPermissions;

    if (userPermissions) {
      sessionStorage.setItem('permissions', JSON.stringify(userPermissions));
    }
  }

  public getPermissions(): Permission {
    if (this.permissions) {
      return this.permissions;
    }

    const permissions = sessionStorage.getItem('permissions');

    if (permissions) {
      this.permissions = JSON.parse(permissions);
      return this.permissions;
    }

    return null;
  }

  private clearPermissions(): void {
    sessionStorage.setItem('permissions', '');
  }

  public setActiveUser(user: User) {
    // Clear information thats not needed
    user.Organization = null;
    user.Credential = null;
    user.Branch = null;
    user.FidelityFund = null;
    user.CopyOfId = null;
    user.IdNumber = null;
    user.TaxNumber = null;

    this.user = user;

    sessionStorage.setItem('user', JSON.stringify(user));
  }

  public getActiveUser(): User {
    if (this.user) {
      return this.user;
    }

    let user = sessionStorage.getItem('user');

    if (user) {
      this.user = JSON.parse(user);

      return this.user;
    }

    return null;
  }

  public setSessionToken(token: string){
    sessionStorage.setItem('session_token', token);
  }

  //code review
  public async resetPassword(
    request: ResetPasswordRqDto
  ): Promise<ResetPasswordRsDto> {
    return new Promise<ResetPasswordRsDto>(async (resolve, reject) => {
      let serviceResponse: ResetPasswordRsDto = null;

      await this.http
        .request<ResetPasswordRsDto>(
          `${this.controllerUrl}/ResetPassword`,
          request,
          'GET',
          true
        )
        .then((response) => {
          if (response.status === 200) {
            serviceResponse = response.body;
          } else {
            return reject(response.error);
          }

          resolve(serviceResponse);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
